<script setup lang="ts">
import { computed, ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n({})

const props = defineProps<{
  editor: import('@tiptap/vue-3').Editor
}>()

const colors = [
  { value: '#fa5555' },
  { value: '#fcc460' },
  { value: '#fafa59' },
  { value: '#80f680' },
  { value: '#6b6bf8' },
  { value: '#803a80' },

  { value: '#ff0000' },
  { value: '#ffa500' },
  { value: '#ffff00' },
  { value: '#00ff00' },
  { value: '#0000ff' },
  { value: '#800080' },

  { value: '#3d0101' },
  { value: '#694401' },
  { value: '#757501' },
  { value: '#027c02' },
  { value: '#01018f' },
  { value: '#360136' },
]
const currentHeadingText: ComputedRef<string> = computed(() => {
  if (currentHeadingValue.value === 0) return t('wysiwyg.headings.normal')
  return t(`wysiwyg.headings.h${currentHeadingValue.value}`)
})
const currentHeadingValue = computed(() => {
  if (props.editor.isActive('heading', { level: 1 })) return 1
  if (props.editor.isActive('heading', { level: 2 })) return 2
  if (props.editor.isActive('heading', { level: 3 })) return 3
  if (props.editor.isActive('heading', { level: 4 })) return 4
  if (props.editor.isActive('heading', { level: 5 })) return 5
  if (props.editor.isActive('heading', { level: 6 })) return 6
  return 0
})
function setColor(color: string | undefined = undefined) {
  if (!color) {
    props.editor.chain().focus().unsetColor().run()
  } else {
    props.editor.chain().focus().setColor(color).run()
  }
}
</script>

<template>
  <v-btn
    icon="mdi-undo"
    variant="text"
    :disabled="!editor.can().undo()"
    class="rounded-0"
    @click="editor.chain().focus().undo().run()"
  />

  <v-btn
    icon="mdi-redo"
    variant="text"
    :disabled="!editor.can().redo()"
    class="rounded-0"
    @click="editor.chain().focus().redo().run()"
  />

  <v-btn
    append-icon="mdi-chevron-down"
    variant="text"
  >
    {{ currentHeadingText }}
    <v-menu activator="parent">
      <v-btn
        :active="!editor.isActive('heading')"
        variant="flat"
        class="rounded-0"
        @click="editor.chain().focus().setParagraph().run()"
      >
        {{ $t('wysiwyg.headings.normal') }}
      </v-btn>

      <v-btn
        :active="editor.isActive('heading', { level: 1 })"
        variant="flat"
        class="rounded-0"
        @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
      >
        <h1>{{ $t('wysiwyg.headings.h1') }}</h1>
      </v-btn>

      <v-btn
        :active="editor.isActive('heading', { level: 2 })"
        variant="flat"
        class="rounded-0"
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
      >
        <h2>{{ $t('wysiwyg.headings.h2') }}</h2>
      </v-btn>

      <v-btn
        :active="editor.isActive('heading', { level: 3 })"
        variant="flat"
        class="rounded-0"
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
      >
        <h3>{{ $t('wysiwyg.headings.h3') }}</h3>
      </v-btn>

      <v-btn
        :active="editor.isActive('heading', { level: 4 })"
        variant="flat"
        class="rounded-0"
        @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
      >
        <h4>{{ $t('wysiwyg.headings.h4') }}</h4>
      </v-btn>

      <v-btn
        :active="editor.isActive('heading', { level: 5 })"
        variant="flat"
        class="rounded-0"
        @click="editor.chain().focus().toggleHeading({ level: 5 }).run()"
      >
        <h5>{{ $t('wysiwyg.headings.h5') }}</h5>
      </v-btn>

      <v-btn
        :active="editor.isActive('heading', { level: 6 })"
        variant="flat"
        class="rounded-0"
        @click="editor.chain().focus().toggleHeading({ level: 6 }).run()"
      >
        <h6>{{ $t('wysiwyg.headings.h6') }}</h6>
      </v-btn>
    </v-menu>
  </v-btn>
  <v-btn
    icon="mdi-format-bold"
    variant="text"
    :active="editor.isActive('bold')"
    class="rounded-0"
    @click="editor.chain().focus().toggleBold().run()"
  />
  <v-btn
    icon="mdi-format-italic"
    variant="text"
    :active="editor.isActive('italic')"
    class="rounded-0"
    @click="editor.chain().focus().toggleItalic().run()"
  />
  <v-btn
    icon="mdi-format-strikethrough"
    variant="text"
    :active="editor.isActive('strike')"
    class="rounded-0"
    @click="editor.chain().focus().toggleStrike().run()"
  />
  <v-btn
    icon="mdi-code-json"
    variant="text"
    :active="editor.isActive('codeBlock')"
    class="rounded-0"
    @click="editor.chain().focus().toggleCodeBlock().run()"
  />
  <v-btn
    variant="text"
    class="rounded-0"
  >
    <v-icon
      icon="mdi-format-color-text"
      :color="editor.getAttributes('textStyle').color"
    />
    <v-menu
      activator="parent"
      max-width="300"
    >
      <v-sheet
        elevation="10"
        class="border"
      >
        <v-row
          :no-gutters="true"
          class="justify-center ma-0"
        >
          <v-col
            v-for="color in colors"
            :key="color.value"
            cols="2"
          >
            <v-btn
              :icon="true"
              :color="color.value"
              size="x-small"
              class="rounded ma-2"
              @click="() => setColor(color.value)"
            >
              <v-icon
                v-if="editor.getAttributes('textStyle').color === color.value"
                class="rounded-circle bg-black"
                icon="mdi-check"
              />
            </v-btn>
          </v-col>
          <v-col cols="12">
            <v-btn
              :block="true"
              variant="text"
              @click="setColor(undefined)"
            >
              {{ $t('wysiwyg.colors.reset') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-sheet>
    </v-menu>
  </v-btn>

  <v-btn
    icon="mdi-marker"
    variant="text"
    :active="editor.isActive('highlight')"
    class="rounded-0"
    @click="editor.chain().focus().toggleHighlight().run()"
  />

  <v-btn
    icon="mdi-format-quote-close"
    variant="text"
    :active="editor.isActive('blockQuote')"
    class="rounded-0"
    @click="editor.chain().focus().toggleBlockquote().run()"
  />

  <v-btn
    icon="mdi-format-list-bulleted"
    variant="text"
    :active="editor.isActive('bulletList')"
    class="rounded-0"
    @click="editor.chain().focus().toggleBulletList().run()"
  />

  <v-btn
    icon="mdi-format-list-numbered"
    variant="text"
    :active="editor.isActive('orderedList')"
    class="rounded-0"
    @click="editor.chain().focus().toggleOrderedList().run()"
  />

  <v-btn
    icon="mdi-format-indent-increase"
    variant="text"
    :disabled="!editor.can().sinkListItem('listItem')"
    class="rounded-0"
    @click="editor.chain().focus().sinkListItem('listItem').run()"
  />
</template>

<style scoped></style>
